import { type ApiEnvelope, api, unwrap } from "@/lib/api";

export const BANNER_TEMPLATES = [
	"hero",
	"promo",
	"product",
	"collection",
	"category",
	"app",
] as const;
export type BannerTemplate = (typeof BANNER_TEMPLATES)[number];

export const BANNER_PLACEMENTS = [
	"hero_homepage",
	"top_strip",
	"category_inset",
	"popin",
] as const;
export type BannerPlacement = (typeof BANNER_PLACEMENTS)[number];

export type BannerStatus =
	| "draft"
	| "scheduled"
	| "active"
	| "paused"
	| "archived";

export interface Banner {
	id: string;
	template: BannerTemplate;
	placement: BannerPlacement;
	status: BannerStatus;
	title: string;
	eyebrow: string | null;
	ctaLabel: string | null;
	ctaHref: string | null;
	color: string;
	imageUrl: string | null;
	imageMobileUrl: string | null;
	imageAlt: string | null;
	productSlug: string | null;
	discount: string | null;
	code: string | null;
	thumbs: string[];
	categorySlugs: string[];
	segmentId: string | null;
	segmentLabel: string | null;
	startsAt: string | null;
	endsAt: string | null;
	sortOrder: number;
	durationSec: number;
	abGroup: string | null;
	abParentId: string | null;
	impressions: number;
	clicks: number;
	conversions: number;
	ctr: number;
	updatedAt: string;
	createdAt: string;
}

export interface BannerDetail extends Banner {
	variants: Banner[];
}

export interface BannerSeries {
	days: Array<{
		day: string;
		impressions: number;
		clicks: number;
		conversions: number;
	}>;
	total: { impressions: number; clicks: number; conversions: number };
}

export interface BannersOverview {
	active: number;
	scheduled: number;
	impressions30d: number;
	clicks30d: number;
	ctr30d: number;
}

export type BannerPayload = Partial<
	Pick<
		Banner,
		| "template"
		| "placement"
		| "title"
		| "eyebrow"
		| "ctaLabel"
		| "ctaHref"
		| "color"
		| "imageUrl"
		| "imageMobileUrl"
		| "imageAlt"
		| "productSlug"
		| "discount"
		| "code"
		| "thumbs"
		| "categorySlugs"
		| "segmentId"
		| "startsAt"
		| "endsAt"
		| "durationSec"
	>
>;

export const bannersApi = {
	list: (params?: { status?: BannerStatus; placement?: BannerPlacement }) =>
		unwrap<{ items: Banner[] }>(
			api.get<ApiEnvelope<{ items: Banner[] }>>("/admin/banners", { params }),
		),

	overview: () =>
		unwrap<BannersOverview>(
			api.get<ApiEnvelope<BannersOverview>>("/admin/banners/overview"),
		),

	detail: (id: string) =>
		unwrap<BannerDetail>(
			api.get<ApiEnvelope<BannerDetail>>(`/admin/banners/${id}`),
		),

	series: (id: string, days = 30) =>
		unwrap<BannerSeries>(
			api.get<ApiEnvelope<BannerSeries>>(`/admin/banners/${id}/series`, {
				params: { days },
			}),
		),

	create: (payload: BannerPayload & { template: BannerTemplate; title: string }) =>
		unwrap<BannerDetail>(
			api.post<ApiEnvelope<BannerDetail>>("/admin/banners", payload),
		),

	update: (id: string, payload: BannerPayload) =>
		unwrap<BannerDetail>(
			api.patch<ApiEnvelope<BannerDetail>>(`/admin/banners/${id}`, payload),
		),

	setStatus: (id: string, status: BannerStatus) =>
		unwrap<BannerDetail>(
			api.post<ApiEnvelope<BannerDetail>>(`/admin/banners/${id}/status`, {
				status,
			}),
		),

	reorder: (order: Array<{ id: string; sortOrder: number }>) =>
		unwrap<{ reordered: number }>(
			api.post<ApiEnvelope<{ reordered: number }>>("/admin/banners/reorder", {
				order,
			}),
		),

	duplicate: (id: string) =>
		unwrap<BannerDetail>(
			api.post<ApiEnvelope<BannerDetail>>(`/admin/banners/${id}/duplicate`),
		),

	createVariant: (id: string) =>
		unwrap<BannerDetail>(
			api.post<ApiEnvelope<BannerDetail>>(`/admin/banners/${id}/ab-test`),
		),

	pickWinner: (id: string) =>
		unwrap<BannerDetail>(
			api.post<ApiEnvelope<BannerDetail>>(`/admin/banners/${id}/ab-winner`),
		),

	importHero: () =>
		unwrap<{ imported: number }>(
			api.post<ApiEnvelope<{ imported: number }>>("/admin/banners/import-hero"),
		),

	archive: (id: string) =>
		unwrap<{ archived: boolean }>(
			api.delete<ApiEnvelope<{ archived: boolean }>>(`/admin/banners/${id}`),
		),
};

/** Libellés français des modèles, avec ce que chacun sert à dire. */
export const TEMPLATE_LABELS: Record<
	BannerTemplate,
	{ name: string; desc: string }
> = {
	hero: {
		name: "Héro éditorial",
		desc: "Grand titre, accroche et bouton. Pour raconter la marque.",
	},
	promo: {
		name: "Promo / Réduction",
		desc: "Pourcentage et code promo. Pour les ventes flash.",
	},
	product: {
		name: "Produit vedette",
		desc: "Un produit, son prix, son visuel. Pour pousser une référence.",
	},
	collection: {
		name: "Collection",
		desc: "Titre et trois vignettes. Pour lancer une gamme.",
	},
	category: {
		name: "Grille de rayons",
		desc: "Pastilles cliquables. Pour la navigation rapide.",
	},
	app: {
		name: "Téléchargement app",
		desc: "Incite à installer l'application mobile.",
	},
};

export const PLACEMENT_LABELS: Record<BannerPlacement, string> = {
	hero_homepage: "Carrousel d'accueil",
	top_strip: "Bandeau haut",
	category_inset: "Encart de rayon",
	popin: "Fenêtre surgissante",
};
